import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Auftrag } from '../types';
import { downloadDateiOderFehler } from '../lib/download';
import AuftragsAkte from './AuftragsAkte';
import { kannZuVertragNavigieren, navigiereZuVertrag } from '../lib/navigation';
import AuftragForm, { AUFTRAG_BEARBEITBARE_STATUS } from './AuftragForm';
import ViewSelector from './ViewSelector';
import RowActionsMenu from './RowActionsMenu';
import './AuftragList.css';

const authToken = () => localStorage.getItem('auth_token') || '';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface AuftragListProps {
  initialAuftragId?: number | null;
  onInitialAuftragConsumed?: () => void;
}

const AuftragList: React.FC<AuftragListProps> = ({ initialAuftragId, onInitialAuftragConsumed }) => {
  const [auftraege, setAuftraege] = useState<Auftrag[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [selectedAuftragId, setSelectedAuftragId] = useState<number | null>(null);
  // Fehlschlag eines Beleg-Downloads (leer = alles gut)
  const [downloadFehler, setDownloadFehler] = useState('');

  // Angebots-/Auftragsmaske (AP 3.1)
  const [showForm, setShowForm] = useState(false);
  const [editingAuftrag, setEditingAuftrag] = useState<Auftrag | null>(null);

  // E-Mail Dialog für Angebot / Auftragsbestätigung
  const [emailDialogId, setEmailDialogId] = useState<number | null>(null);
  const [emailDialogType, setEmailDialogType] = useState<'angebot' | 'auftragsbestaetigung' | null>(null);
  const [emailInput, setEmailInput] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailFeedback, setEmailFeedback] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialAuftragId != null) {
      setSelectedAuftragId(initialAuftragId);
      onInitialAuftragConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAuftragId]);

  const loadAuftraege = useCallback(async () => {
    setLoading(true);
    try {
      const url = filterStatus
        ? `${API_URL}/api/auftraege?status=${filterStatus}`
        : `${API_URL}/api/auftraege`;
      const response = await axios.get(url);
      setAuftraege(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Aufträge:', error);
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => {
    loadAuftraege();
  }, [loadAuftraege]);

  const getStatusClass = (status: string) => {
    const map: Record<string, string> = {
      'Angebot': 'auftrag-status-angebot',
      'Bestätigt': 'auftrag-status-bestaetigt',
      'In Produktion': 'auftrag-status-produktion',
      'Versandbereit': 'auftrag-status-versand',
      'Geliefert': 'auftrag-status-geliefert',
      'Storniert': 'auftrag-status-storniert',
    };
    return map[status] || '';
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE');
  };

  const formatPrice = (price?: number) => {
    if (price === null || price === undefined) return '-';
    return `${price.toFixed(2)} \u20AC`;
  };

  const nr4 = (id: number) => String(id).padStart(4, '0');

  /** Beleg-PDF holen; den Dateinamen bestimmt der Server (Content-Disposition). */
  const belegHerunterladen = async (
    auftrag: Auftrag,
    typ: 'angebot' | 'auftragsbestaetigung' | 'lieferschein',
    kuerzel: string,
    bezeichnung: string,
  ) => {
    const id = auftrag.auftrag_id!;
    setDownloadFehler('');
    const fehler = await downloadDateiOderFehler(
      `${API_URL}/api/dokumente/auftraege/${id}/${typ}/pdf`,
      `${kuerzel}_${auftrag.auftrag_nr || nr4(id)}.pdf`,
      bezeichnung,
    );
    if (fehler) setDownloadFehler(fehler);
  };

  const openEmailDialog = (id: number, type: 'angebot' | 'auftragsbestaetigung') => {
    setEmailDialogId(emailDialogId === id && emailDialogType === type ? null : id);
    setEmailDialogType(type);
    setEmailInput('');
  };

  const sendEmail = async (auftragId: number) => {
    if (!emailDialogType) return;
    setEmailSending(true);
    const key = `${auftragId}-${emailDialogType}`;
    try {
      await axios.post(`${API_URL}/api/email/${emailDialogType}/${auftragId}`, { empfaenger: emailInput || undefined });
      setEmailFeedback(prev => ({ ...prev, [key]: 'E-Mail gesendet' }));
      setEmailDialogId(null);
    } catch (e: any) {
      setEmailFeedback(prev => ({ ...prev, [key]: e.response?.data?.detail || 'Fehler' }));
      setEmailDialogId(null);
    } finally {
      setEmailSending(false);
    }
  };

  const openNeuesAngebot = () => {
    setEditingAuftrag(null);
    setShowForm(true);
  };

  const openBearbeiten = (e: React.MouseEvent, auftrag: Auftrag) => {
    e.stopPropagation();
    setEditingAuftrag(auftrag);
    setShowForm(true);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingAuftrag(null);
    loadAuftraege();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingAuftrag(null);
    // Reload: falls sich der Status waehrend der Bearbeitung geaendert hat
    // (z.B. serverseitiges Gate hat zwischenzeitlich gegriffen), soll die
    // Liste keine veraltete "Bearbeiten"-Sichtbarkeit mehr zeigen.
    loadAuftraege();
  };

  if (selectedAuftragId !== null) {
    return (
      <AuftragsAkte
        auftragId={selectedAuftragId}
        onOpenVertrag={kannZuVertragNavigieren() ? navigiereZuVertrag : undefined}
        onBack={() => {
          setSelectedAuftragId(null);
          loadAuftraege();
        }}
      />
    );
  }

  if (showForm) {
    return (
      <AuftragForm
        editingAuftrag={editingAuftrag}
        onSuccess={handleFormSuccess}
        onCancel={handleFormCancel}
      />
    );
  }

  return (
    <div className="auftrag-list-container">
      <div className="list-header">
        <h2>Aufträge</h2>
        <div className="header-actions">
          <ViewSelector
            bereich="auftraege"
            authToken={authToken()}
            currentFilter={{ status: filterStatus }}
            onFilterChange={(filter) => setFilterStatus((filter.status as string) || '')}
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="">Alle Status</option>
            <option value="Angebot">Angebot</option>
            <option value="Bestätigt">Bestätigt</option>
            <option value="In Produktion">In Produktion</option>
            <option value="Versandbereit">Versandbereit</option>
            <option value="Geliefert">Geliefert</option>
            <option value="Storniert">Storniert</option>
          </select>
          <button className="k-btn k-btn--primary" onClick={openNeuesAngebot}>+ Neues Angebot</button>
        </div>
      </div>

      {downloadFehler && <div className="k-alert k-alert--danger">{downloadFehler}</div>}

      {loading ? (
        <div className="loading">Lädt...</div>
      ) : auftraege.length === 0 ? (
        <div className="empty-state">
          <p>Keine Aufträge vorhanden</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="auftrag-table">
            <thead>
              <tr>
                <th>Auftrag-Nr.</th>
                <th>Kunde</th>
                <th>Status</th>
                <th>Liefertermin</th>
                <th>Priorität</th>
                <th>Gesamt</th>
                <th>Erstellt</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {auftraege.map((auftrag) => (
                <React.Fragment key={auftrag.auftrag_id}>
                  <tr
                    className="auftrag-row"
                    onClick={() => auftrag.auftrag_id && setSelectedAuftragId(auftrag.auftrag_id)}
                  >
                    <td className="auftrag-nr">{auftrag.auftrag_nr}</td>
                    <td>{auftrag.kunde?.name || '-'}</td>
                    <td>
                      <span className={`status-badge ${getStatusClass(auftrag.status)}`}>
                        {auftrag.status}
                      </span>
                    </td>
                    <td>{formatDate(auftrag.liefertermin)}</td>
                    <td>
                      <span className={`prio-badge prio-${auftrag.prioritaet >= 7 ? 'high' : auftrag.prioritaet >= 4 ? 'medium' : 'low'}`}>
                        {auftrag.prioritaet}
                      </span>
                    </td>
                    <td className="price-cell">{formatPrice(auftrag.gesamtsumme)}</td>
                    <td>{formatDate(auftrag.erstellt_am)}</td>
                    <td className="action-cell" onClick={(e) => e.stopPropagation()}>
                      {emailFeedback[`${auftrag.auftrag_id}-angebot`] && (
                        <span className="email-feedback-small">{emailFeedback[`${auftrag.auftrag_id}-angebot`]}</span>
                      )}
                      {emailFeedback[`${auftrag.auftrag_id}-auftragsbestaetigung`] && (
                        <span className="email-feedback-small">{emailFeedback[`${auftrag.auftrag_id}-auftragsbestaetigung`]}</span>
                      )}
                      {AUFTRAG_BEARBEITBARE_STATUS.includes(auftrag.status) && (
                        <button
                          className="btn-edit"
                          onClick={(e) => openBearbeiten(e, auftrag)}
                          title="Angebot/Auftrag bearbeiten"
                        >
                          Bearbeiten
                        </button>
                      )}
                      {auftrag.auftrag_id && (
                        <RowActionsMenu
                          actions={[
                            { label: 'Angebot herunterladen', onClick: () => belegHerunterladen(auftrag, 'angebot', 'AN', 'Angebot') },
                            { label: 'Angebot per E-Mail senden', onClick: () => openEmailDialog(auftrag.auftrag_id!, 'angebot') },
                            { label: 'Auftragsbestätigung herunterladen', onClick: () => belegHerunterladen(auftrag, 'auftragsbestaetigung', 'AB', 'Auftragsbestätigung') },
                            { label: 'Auftragsbestätigung per E-Mail senden', onClick: () => openEmailDialog(auftrag.auftrag_id!, 'auftragsbestaetigung') },
                            { label: 'Lieferschein herunterladen', onClick: () => belegHerunterladen(auftrag, 'lieferschein', 'LS', 'Lieferschein') },
                          ]}
                        />
                      )}
                    </td>
                  </tr>
                  {emailDialogId === auftrag.auftrag_id && (
                    <tr className="detail-row">
                      <td colSpan={8}>
                        <div className="email-inline-dialog-auftrag">
                          <span style={{ fontWeight: 600 }}>
                            {emailDialogType === 'angebot' ? 'Angebot' : 'Auftragsbestätigung'} per E-Mail
                          </span>
                          <input
                            type="email"
                            placeholder="Empfänger-E-Mail (leer = Kunden-E-Mail)"
                            value={emailInput}
                            onChange={e => setEmailInput(e.target.value)}
                            className="email-inline-input-auftrag"
                            autoFocus
                          />
                          <button
                            className="btn-pdf-download"
                            onClick={() => auftrag.auftrag_id && sendEmail(auftrag.auftrag_id)}
                            disabled={emailSending}
                          >
                            {emailSending ? 'Sendet...' : 'Senden'}
                          </button>
                          <button className="btn-pdf-download" onClick={() => setEmailDialogId(null)}>
                            Abbrechen
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AuftragList;
